import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import intlShape from '@/shared/utils/intlShape';
import classnames from 'classnames';
import { FaUndo, FaFilter } from 'react-icons/fa';
import UserSearch from '@/shared/components/input/UserSearch';
import RelationListPicker from '@/shared/components/input/RelationListPicker';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import MultiSelect from '@/shared/components/input/multiselect/MultiSelect';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import config from '@/config';
import isEqual from 'lodash-es/isEqual';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import api from '@/shared/api';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import RelationSearch from '@/shared/components/input/RelationSearch';
import DataCacheService from '@/shared/services/DataCacheService';
import AssignmentTemplateSelector from '@/shared/components/input/AssignmentTemplateSelector';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import InvoiceScheduleTypeMultiSelect from '@/shared/components/input/multiselect/InvoiceScheduleTypeMultiSelect';
import guid from '@/shared/utils/guid';
import EndFiscalYearMultiSelect from '@/shared/components/input/multiselect/EndFiscalYearMultiSelect';
import messages from './invoiceStreamMessages';
import RelationGroupMultiSelect from '@/shared/components/input/multiselect/RelationGroupMultiSelect';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '@/shared/components/DynamicFilter';
import { Dropdown } from 'react-bootstrap';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import userStorage from '@/shared/services/userStorage';
import Toggle from 'react-toggle';

const dynamicFiltersStorageKey = 'InvoiceStreamDynamicFilters';

export const defaultInvoiceStreamFilters = {
  priceGridId: null,
  associate: null, // UserId
  amountFrom: '',
  amountTo: '',
  assistant: null, // UserId
  assistantBackup: null, // UserId
  responsible: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  relationId: null, // String
  relationName: null,
  type: [], // Array
  includeInactive: false,
  withLabels: [], // Array
  withoutLabels: [], // Array,
  ownCompanies: [],
  assignmentTemplateId: null,
  vatPeriodicities: [],
  invoiceScheduleTypes: [],
  endFiscalYears: [],
  filterBehaviours: {
    withLabels: 1,
  },
  relationGroups: [],
  relations: [],
  relationStartDateFrom: null,
  relationStartDateUntil: null,
};

const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(
    defaultInvoiceStreamFilters,
    'relationTypes',
    'invoiceScheduleTypes',
  ),
  { id: 'includeInactive', label: messages.IncludeInactiveRelations },
  { id: 'priceGridId', label: messages.PriceGrid },
  { id: 'amountFrom', label: messages.AmountFrom },
  { id: 'relationStartDateFrom', label: messages.RelationStartDateFrom },
  { id: 'relationPicker', label: messages.RelationPicker },
];

class InvoiceStreamFilters extends Component {
  state = {
    amountFrom: null,
    amountTo: null,
    labels: [],
    dynamicFilters: [],
    selectedRelations: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      assistantBackup: PropTypes.string,
      associate: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      relationId: PropTypes.string,
      relationName: PropTypes.string,
      includeInactive: PropTypes.bool,
      type: PropTypes.arrayOf(PropTypes.number),
      crmTypes: PropTypes.arrayOf(PropTypes.number),
      legalForms: PropTypes.arrayOf(PropTypes.number),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      amountFrom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      amountTo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      linkType: PropTypes.number,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      assignmentTemplateId: PropTypes.string,
      vatPeriodicities: PropTypes.arrayOf(PropTypes.number),
      invoiceScheduleTypes: PropTypes.arrayOf(PropTypes.string),
      endFiscalYears: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      relations: PropTypes.arrayOf(PropTypes.string),
      relationStartDateFrom: PropTypes.oneOfType([PropTypes.string, momentPropTypes.momentObj]),
      relationStartDateUntil: PropTypes.oneOfType([PropTypes.string, momentPropTypes.momentObj]),
    }),
    intl: intlShape.isRequired,
    countSelectedRelations: PropTypes.string.isRequired,
  };

  static defaultProps = {
    filters: defaultInvoiceStreamFilters,
  };

  constructor(props) {
    super(props);
    const { filters } = props;
    this.state.amountFrom =
      !filters.amountFrom && filters.amountFrom !== 0 ? '' : `${filters.amountFrom}`;
    this.state.amountTo = !filters.amountTo && filters.amountTo !== 0 ? '' : `${filters.amountTo}`;
  }

  componentDidMount() {
    const { intl, filters } = this.props;
    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    const labelPromise = api.base.get(`${config.appUrls.api}/api/Resource/Tags`);
    const relationsPromise =
      filters.relations?.length > 0
        ? api.base.post(`${config.appUrls.api}/api/relation/names`, filters.relations)
        : Promise.resolve({ data: [] });

    Promise.all([labelPromise, relationsPromise]).then(
      ([{ data: labels }, { data: relationNames }]) => {
        this.setState({
          labels: labels
            .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
            .sort(dynamicSort('sort')),
          dynamicFilters: initDynamicFilters(
            intl,
            allDynamicFilters,
            userStorage.getItem(dynamicFiltersStorageKey) || [],
            filters,
            defaultInvoiceStreamFilters,
          ),
          selectedRelations: relationNames ?? [],
        });
      },
    );
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.filters.relations?.length > 0 &&
      (!this.props.filters.relations || this.props.filters.relations.length === 0)
    ) {
      this.setState({ selectedRelations: [] });
    }
  }

  dynamicFilterChanged = (item, selected, visibleItemIds) => {
    const { dynamicFilters } = this.state;
    const { filters } = this.props;

    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      if (visibleItemIds && !visibleItemIds.includes(x.id)) return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({
      dynamicFilters: newFilters,
    });

    if (
      !selected &&
      JSON.stringify(filters[item.id]) !== JSON.stringify(defaultInvoiceStreamFilters[item.id])
    ) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultInvoiceStreamFilters[item.id] });
    }
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (!isEqual(filters, newFilters)) {
      onFilterChange(newFilters);
    }
  };

  filterRelationTypes = relType => {
    const { filters = {} } = this.props;
    const types = filters.type || [];

    const idx = types.indexOf(relType);
    if (idx >= 0) {
      this.filterChanged({ type: types.filter((_, i) => i !== idx) });
    } else {
      this.filterChanged({ type: [...types, relType] });
    }
  };

  filterCrmTypes = crmType => {
    const { filters = {} } = this.props;
    const types = filters.crmTypes || [];

    const idx = types.indexOf(crmType);
    if (idx >= 0) {
      this.filterChanged({ crmTypes: types.filter((_, i) => i !== idx) });
    } else {
      this.filterChanged({ crmTypes: [...types, crmType] });
    }
  };

  withLabelsFilterRemoved = item => {
    const { filters } = this.props;
    const { withLabels } = filters;
    const index = withLabels.findIndex(x => x === item.value.label);

    const copy = [...withLabels];
    copy.splice(index, 1);

    this.filterChanged({ withLabels: copy });
  };

  withLabelsFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { withLabels } = filters;

    if (!withLabels) {
      this.filterChanged({
        withLabels: [selectedItem.label],
      });
    } else {
      if (withLabels.includes(selectedItem.label)) return;
      this.filterChanged({
        withLabels: [...withLabels, selectedItem.label],
      });
    }
  };

  withoutLabelsFilterRemoved = item => {
    const { filters } = this.props;
    const { withoutLabels } = filters;
    const index = withoutLabels.findIndex(x => x === item.value.label);

    const copy = [...withoutLabels];
    copy.splice(index, 1);

    this.filterChanged({ withoutLabels: copy });
  };

  withoutLabelsFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { withoutLabels } = filters;

    if (!withoutLabels) {
      this.filterChanged({
        withoutLabels: [selectedItem.label],
      });
    } else {
      if (withoutLabels.includes(selectedItem.label)) return;
      this.filterChanged({
        withoutLabels: [...withoutLabels, selectedItem.label],
      });
    }
  };

  getRelationTypes = () => {
    const { intl } = this.props;
    return [
      {
        id: 0,
        label: intl.formatMessage(listItemMsg('relationtype', 0, 'Company')),
      },
      {
        id: 1,
        label: intl.formatMessage(listItemMsg('relationtype', 1, 'SoleProprietor')),
      },
      {
        id: 2,
        label: intl.formatMessage(listItemMsg('relationtype', 2, 'Person')),
      },
    ];
  };

  handleAmountBlur = amountToOrFrom => {
    const { amountFrom, amountTo } = this.state;
    if (amountToOrFrom === 'amountFrom') {
      this.filterChanged({
        amountFrom,
      });
    }

    if (amountToOrFrom === 'amountTo') {
      this.filterChanged({
        amountTo,
      });
    }
  };

  handleAmountKeyDown = (e, amountToOrFrom) => {
    const { filters } = this.props;
    // Escape
    if (e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();

      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
      if (amountToOrFrom === 'amountFrom') {
        this.setState({ amountFrom: filters.amountFrom });
      }

      if (amountToOrFrom === 'amountTo') {
        this.setState({ amountTo: filters.amountTo });
      }
    }
    // Enter
    else if (e.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
      this.handleAmountBlur(amountToOrFrom);
    }
  };

  resetFilters = () => {
    const { onFilterChange } = this.props;
    this.setState({ amountFrom: '', amountTo: '', selectedRelations: [] }, () => {
      onFilterChange(defaultInvoiceStreamFilters);
    });
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'priceGridId': {
        const { intl, priceGrids } = this.props;
        const selectedPriceGrid = filters.priceGridId
          ? priceGrids.find(x => x.key === filters.priceGridId)
          : null;
        return (
          <div className="col-md-4">
            <div className="form-group">
              <AutoComplete
                onChange={e =>
                  this.filterChanged({
                    priceGridId: e && e.key ? e.key : null,
                  })
                }
                onClear={() => this.filterChanged({ priceGridId: null })}
                value={selectedPriceGrid}
                items={priceGrids}
                placeholder={intl.formatMessage(messages.PriceGrid)}
                mode="edit"
              />
            </div>
          </div>
        );
      }
      case 'relationStartDateFrom': {
        const relationStartDateFrom =
          typeof filters.relationStartDateFrom === 'string'
            ? moment.utc(filters.relationStartDateFrom)
            : filters.relationStartDateFrom;
        const relationStartDateUntil =
          typeof filters.relationStartDateUntil === 'string'
            ? moment.utc(filters.relationStartDateUntil)
            : filters.relationStartDateUntil;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={relationStartDateFrom}
                startDateId="InvoiceStreamOverviewstartDateFrom"
                endDate={relationStartDateUntil}
                endDateId="InvoiceStreamOverviewstartDateUntil"
                onDatesChange={({ startDate, endDate }) =>
                  this.filterChanged({
                    relationStartDateFrom: startDate?.format('YYYY-MM-DD'),
                    relationStartDateUntil: endDate?.format('YYYY-MM-DD'),
                  })
                }
                startDatePlaceholderText={intl.formatMessage(messages.RelationStartDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.RelationStartDateUntil)}
                block
              />
            </div>
          </div>
        );
      }
      case 'amountFrom': {
        const { amountFrom, amountTo } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="col-md-6" style={{ padding: '0px' }}>
              <div className="input-group">
                <span className="input-group-addon">{intl.formatMessage(messages.AmountFrom)}</span>

                <input
                  type="number"
                  onBlur={() => this.handleAmountBlur('amountFrom')}
                  onKeyDown={e => {
                    const event = e;
                    this.handleAmountKeyDown(event, 'amountFrom');
                  }}
                  onChange={e => this.setState({ amountFrom: e.target.value })}
                  value={amountFrom}
                  className="form-control"
                  style={{ textAlign: 'right' }}
                  placeholder={intl.formatMessage(messages.Amount)}
                />
              </div>
            </div>
            <div className="col-md-6" style={{ padding: '0px' }}>
              <div className="input-group">
                <span className="input-group-addon">{intl.formatMessage(messages.AmountTo)}</span>
                <input
                  type="number"
                  className="form-control"
                  onBlur={() => this.handleAmountBlur('amountTo')}
                  onKeyDown={e => {
                    const event = e;
                    this.handleAmountKeyDown(event, 'amountTo');
                  }}
                  onChange={e => this.setState({ amountTo: e.target.value })}
                  value={amountTo}
                  style={{ textAlign: 'right' }}
                  placeholder={intl.formatMessage(messages.Amount)}
                />
              </div>
            </div>
          </div>
        );
      }
      case 'includeInactive':
        return (
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <label className="react-toggle-wrapper">
                <Toggle
                  checked={filters.includeInactive}
                  onChange={e => this.filterChanged({ includeInactive: !!e.target.checked })}
                  icons={false}
                />
                <FormattedMessage className="label-text" {...messages.IncludeInactiveRelations} />
              </label>
            </div>
          </div>
        );
      case 'relationPicker': {
        const { selectedRelations } = this.state;

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <RelationListPicker
                placeholder={intl.formatMessage(messages.ChooseRelations)}
                relations={selectedRelations || []}
                onAddRelation={relation => {
                  this.setState({ selectedRelations: [...selectedRelations, relation] }, () => {
                    this.filterChanged({
                      relations: [...filters.relations, relation.id],
                    });
                  });
                }}
                onRemoveRelation={relation => {
                  this.setState(
                    { selectedRelations: selectedRelations.filter(x => x.id !== relation.id) },
                    () => {
                      this.filterChanged({
                        relations: filters.relations.filter(x => x !== relation.id),
                      });
                    },
                  );
                }}
              />
            </div>
          </div>
        );
      }
      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  render() {
    const { filters, intl, countSelectedRelations } = this.props;
    const { labels, dynamicFilters } = this.state;

    const defaultBtnClass = 'btn btn-default';
    const relationTypes = this.getRelationTypes();
    const hasFilters = !isEqual(filters, defaultInvoiceStreamFilters);

    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div role="group" aria-label="relationType" className="btn-group btn-group-justified">
              {relationTypes.map(t => (
                <button
                  key={`RelTypeFilter${t.id}`}
                  onClick={() => {
                    document.activeElement.blur();
                    this.filterRelationTypes(t.id);
                  }}
                  type="button"
                  title={t.label}
                  className={classnames(defaultBtnClass, {
                    active: filters && filters.type && filters.type.includes(t.id),
                  })}
                >
                  <RelationTypeIcon type={t.id} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <InvoiceScheduleTypeMultiSelect
                selection={filters.invoiceScheduleTypes}
                onChange={items => this.filterChanged({ invoiceScheduleTypes: items })}
              />
            </div>
          </div>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                filters={filters}
                labels={labels}
                collClass="col-lg-4 col-sm-6"
                filterChanged={this.filterChanged}
                renderCustomFilter={this.renderDynamicFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="row hidden-xs hidden-sm">
          <div className="col-sm-12">
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon margin5R" />
                <FormattedMessage {...messages.MoreFilters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={this.resetFilters}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
            <div className="margin-10B content-right pull-right">
              <span>{countSelectedRelations}</span>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(InvoiceStreamFilters);
